import React, { useState } from 'react';
import { clientsWithLogos, clientsWithoutLogos } from '../mock';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Building2, Eye } from 'lucide-react';

const ClientsSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <section id="clients" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            Our <span className="text-blue-600">Clients</span>
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Trusted by India's leading power generation and distribution companies
          </p>
        </div>

        {/* Clients with Logos - Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {clientsWithLogos.map((client, index) => (
            <div
              key={index}
              className="group bg-slate-50 hover:bg-white border-2 border-slate-200 hover:border-blue-600 rounded-lg p-6 transition-all duration-300 hover:shadow-xl cursor-pointer flex flex-col items-center"
            >
              <div className="w-full h-32 flex items-center justify-center mb-4 bg-white rounded-lg p-4">
                <img
                  src={client.logo}
                  alt={client.name}
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
              <h3 className="text-sm font-semibold text-slate-800 group-hover:text-blue-600 transition-colors text-center">
                {client.name}
              </h3>
            </div>
          ))}
        </div>

        {/* Other Clients - Scrolling Marquee */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-bold text-slate-800">
              Other Prestigious Clients
            </h3>
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  View All Clients
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-slate-800">
                    Our Complete Client Portfolio
                  </DialogTitle>
                </DialogHeader>
                
                {/* Clients with Logos in Modal */}
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-slate-700 mb-4">Featured Clients</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {clientsWithLogos.map((client, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-4 p-4 bg-slate-50 border border-slate-200 rounded-lg"
                      >
                        <div className="w-16 h-16 flex items-center justify-center bg-white rounded-lg p-2">
                          <img
                            src={client.logo}
                            alt={client.name}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-800">{client.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* All Other Clients in Modal */}
                <div className="mt-8">
                  <h4 className="text-lg font-semibold text-slate-700 mb-4">All Prestigious Clients</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {clientsWithoutLogos.map((client, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-600 transition-colors"
                      >
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <p className="text-sm font-medium text-slate-700">{client}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="relative overflow-hidden bg-slate-50 py-8 border-y-2 border-slate-200">
            <div className="flex animate-scroll whitespace-nowrap">
              {/* First set of clients */}
              {clientsWithoutLogos.map((client, index) => (
                <div
                  key={`first-${index}`}
                  className="inline-flex items-center px-8 mx-4 py-4 bg-white border-2 border-slate-200 rounded-lg shadow-sm"
                >
                  <span className="text-slate-700 font-medium">{client}</span>
                </div>
              ))}
              {/* Duplicate for seamless loop */}
              {clientsWithoutLogos.map((client, index) => (
                <div
                  key={`second-${index}`}
                  className="inline-flex items-center px-8 mx-4 py-4 bg-white border-2 border-slate-200 rounded-lg shadow-sm"
                >
                  <span className="text-slate-700 font-medium">{client}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-8 max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">
              Join Our Growing List of Satisfied Clients
            </h3>
            <p className="text-slate-600 mb-6">
              Partner with us for reliable, timely, and quality power plant solutions
            </p>
            <button
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              Start a Project
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientsSection;
